/**
 * useStreamingBlueprint — small hook the four Blueprint generation pages
 * share. Decides between sync POST and SSE streaming, exposes a single
 * `submit(payload)` plus the four state fields the pages render.
 *
 * Streaming sets `streamRequest` which the page consumes by mounting
 * `<StreamingMarkdown>`; on done it calls back into the hook to set the
 * final `blueprint` payload.
 *
 * The pure fetch logic lives in `postBlueprintSync` below — it's exported
 * for unit testing (no React). The hook is just React state glue around it.
 */

import { useState } from "react";
import { parseJsonResponse } from "./api-fetch.js";

/**
 * Pure async — POST a Blueprint payload and return `{ ok, body }`.
 * Exported for unit testing without React.
 *
 * Bug 2026-05-07 (#54): the original implementation used a bare
 * `await res.json()` and synthesized `{ error: "Non-JSON response" }`
 * on parse failure. That hid the underlying cause (almost always a
 * Netlify function timeout returning HTML) behind a useless message.
 * Now uses `parseJsonResponse` from `api-fetch.js` which surfaces the
 * actual HTTP status + a helpful hint ("server error (HTTP 504) —
 * request timed out — try the Streaming option") so failures point at
 * the cause instead of the symptom.
 */
export async function postBlueprintSync({ url, bearerToken, payload, fetchImpl = fetch }) {
  const res = await fetchImpl(url, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
    body: JSON.stringify(payload),
  });
  const body = await parseJsonResponse(res);
  return { ok: res.ok, body };
}

/**
 * @param {object} opts
 * @param {string} opts.syncUrl
 * @param {string} opts.streamUrl
 * @param {string} opts.bearerToken
 * @param {boolean} [opts.defaultStreaming=true] Default Tier 1 surfaces to
 *   streaming. Sync routes for Pre-Meeting / Resume / Relational / Conflict
 *   Mediation all run 30–50s and reliably exceed Netlify's 26s function
 *   timeout. Streaming is the only path that fits in V1 budgets. Pages can
 *   override by passing `defaultStreaming: false` if they have a specific
 *   reason (none currently do — flagged here so future callers think
 *   carefully before flipping it). Bug #54 (2026-05-07).
 */
export function useStreamingBlueprint({ syncUrl, streamUrl, bearerToken, defaultStreaming = true }) {
  const [streaming, setStreaming] = useState(defaultStreaming);
  const [generating, setGenerating] = useState(false);
  const [streamRequest, setStreamRequest] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [error, setError] = useState(null);

  // Always-sync POST. Used by callers that need a sync regen regardless of
  // the streaming toggle (e.g. Relational Flip mode swaps the rendered
  // Blueprint in place — streaming would unmount the article).
  const submitSync = async (payload) => {
    setError(null);
    setGenerating(true);
    try {
      const result = await postBlueprintSync({ url: syncUrl, bearerToken, payload });
      if (!result.ok) setError(result.body.error || "Generation failed");
      else setBlueprint(result.body);
      return result;
    } catch (e) {
      setError(e.message);
      return { ok: false, body: { error: e.message } };
    } finally {
      setGenerating(false);
    }
  };

  // Streaming-aware POST. Routes to SSE when `streaming` is true; otherwise
  // delegates to submitSync.
  const submit = async (payload) => {
    setError(null);
    if (streaming) {
      setStreamRequest(payload);
      return;
    }
    return submitSync(payload);
  };

  const onStreamDone = (payload) => {
    setBlueprint(payload);
    setStreamRequest(null);
  };

  const reset = () => {
    setBlueprint(null);
    setStreamRequest(null);
    setError(null);
  };

  // Retry the in-flight streaming payload. Used by the friendly Regenerate
  // affordance after a brand-discipline scrubber trip (task #62). Cycles
  // streamRequest through null first so StreamingMarkdown unmounts +
  // remounts with a fresh extractor / accumulator / phase.
  const retry = () => {
    if (!streamRequest) return;
    const payload = streamRequest;
    setError(null);
    setStreamRequest(null);
    // Defer the re-submit so the unmount commits before the remount.
    // setTimeout(0) is intentional — a synchronous setStreamRequest(payload)
    // here would coalesce into the same render and skip the unmount.
    setTimeout(() => setStreamRequest(payload), 0);
  };

  return {
    streaming, setStreaming,
    generating,
    streamRequest, streamUrl,
    blueprint, setBlueprint,
    error, setError,
    submit,
    submitSync,
    onStreamDone,
    reset,
    retry,
  };
}
